import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScoreBar } from '../components/search/useSortableResults';
import { CompetencyBadges, SectionHeader } from '../components/search/ResultDialogShell';
import CompanySearchBar from '../components/companies/CompanySearchBar';
import { getAllCompanies, searchCompanies } from '../services/apiService';
import type { CompanyListItem } from '../types/api';

const PAGE_SIZE = 100;
const MIN_SIMILARITY = 0.6;

type Op = 'AND' | 'OR';

function readQueryFromParams(
  params: URLSearchParams,
): { keywords: string[]; ops: Op[] } | null {
  const q = params.get('q');
  if (!q) return null;
  const keywords = q.split(',').map((s) => s.trim()).filter(Boolean);
  if (keywords.length === 0) return null;
  const opsRaw = params.get('ops');
  const ops: Op[] = opsRaw
    ? opsRaw.split(',').map((o) => (o.trim().toUpperCase() === 'OR' ? 'OR' : 'AND'))
    : keywords.slice(1).map(() => 'AND' as Op);
  return { keywords, ops };
}

function queryToBarValue(keywords: string[], ops: Op[]): string {
  return keywords
    .map((kw, i) => (i === 0 ? kw : `${ops[i - 1] ?? 'AND'} ${kw}`))
    .join(' ');
}

const KATPO_MAP: Record<string, [string, number]> = {
  '000': ['N/A', 0],
  '110': ['0', 0],
  '120': ['1-5', 5],
  '130': ['6-9', 9],
  '210': ['10-19', 19],
  '220': ['20-24', 24],
  '230': ['25-49', 49],
  '240': ['50-99', 99],
  '310': ['100-199', 199],
  '320': ['200-249', 249],
  '330': ['250-499', 499],
  '340': ['500-999', 999],
  '410': ['1000-1499', 1499],
  '420': ['1500-1999', 1999],
  '430': ['2000-2499', 2499],
  '440': ['2500-2999', 2999],
  '450': ['3000-3999', 3999],
  '460': ['4000-4999', 4999],
  '470': ['5000-9999', 9999],
  '510': ['10000+', 10000],
};

function normalizeKatpo(k: string | null | undefined): string | null {
  if (!k) return null;
  return String(parseInt(k, 10));
}

function employeeRange(katpo: string | null | undefined): string {
  const key = normalizeKatpo(katpo);
  if (!key) return '';
  return KATPO_MAP[key]?.[0] ?? '';
}

function employeeSortValue(katpo: string | null | undefined): number {
  const key = normalizeKatpo(katpo);
  if (!key) return -1;
  return KATPO_MAP[key]?.[1] ?? -1;
}

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

type SortKey = 'firma' | 'nace' | 'obec_text' | 'katpo' | 'similarity';
type SortDir = 'asc' | 'desc';

function sortValue(
  c: CompanyListItem,
  key: SortKey,
  lang: string,
): string | number {
  switch (key) {
    case 'firma':
      return (c.firma || '').toLowerCase();
    case 'nace':
      return ((lang === 'cs' ? c.nace_cz : c.nace_en) || '').toLowerCase();
    case 'obec_text':
      return (c.obec_text || '').toLowerCase();
    case 'katpo':
      return employeeSortValue(c.katpo);
    case 'similarity':
      return c.similarity ?? 0;
  }
}

export default function CompaniesPage() {
  const { t, i18n } = useTranslation('companies');
  const [searchParams, setSearchParams] = useSearchParams();

  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const initialQuery = useMemo(() => readQueryFromParams(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [searchResults, setSearchResults] = useState<CompanyListItem[] | null>(null);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [barInitialValue] = useState<string | undefined>(
    initialQuery ? queryToBarValue(initialQuery.keywords, initialQuery.ops) : undefined,
  );

  const [filterText, setFilterText] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>(initialQuery ? 'similarity' : 'firma');
  const [sortDir, setSortDir] = useState<SortDir>(initialQuery ? 'desc' : 'asc');
  const [page, setPage] = useState<number>(1);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const inSearchMode = searchResults !== null;

  function toggleExpanded(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAllCompanies()
      .then((data) => {
        if (cancelled) return;
        setCompanies(data.results || []);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load companies');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterText, sortKey, sortDir, companies, searchResults]);

  async function runSearch(keywords: string[], ops: Op[]) {
    setSearchLoading(true);
    setSearchError(null);
    setExpandedIds(new Set());
    setSortKey('similarity');
    setSortDir('desc');
    try {
      const data = await searchCompanies(keywords, ops);
      const filtered = (data.results || []).filter(
        (r) => (r.similarity ?? 0) >= MIN_SIMILARITY,
      );
      setSearchResults(filtered);
    } catch (err: any) {
      setSearchResults([]);
      setSearchError(err?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  }

  function resetToBrowse() {
    setSearchResults(null);
    setSearchError(null);
    setSearchLoading(false);
    setExpandedIds(new Set());
    setSortKey('firma');
    setSortDir('asc');
    setSearchParams(new URLSearchParams());
  }

  function handleSearch(keywords: string[], ops: Op[]) {
    if (keywords.length === 0) {
      resetToBrowse();
      return;
    }
    const next = new URLSearchParams();
    next.set('q', keywords.join(','));
    if (ops.length > 0 && ops.some((o) => o === 'OR')) {
      next.set('ops', ops.join(','));
    }
    setSearchParams(next);
    runSearch(keywords, ops);
  }

  // Run the initial search from URL params on mount (shareable links / reloads).
  useEffect(() => {
    if (initialQuery) {
      runSearch(initialQuery.keywords, initialQuery.ops);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const lang = i18n.language;

  // The plain substring filter refines whichever set is currently shown:
  // search results (when in search mode) or the full browse list.
  const baseSet = searchResults ?? companies;

  const filtered = useMemo(() => {
    const q = normalize(filterText.trim());
    if (!q) return baseSet;
    return baseSet.filter((c) => {
      const haystack = normalize(
        [
          c.firma || '',
          (lang === 'cs' ? c.nace_cz : c.nace_en) || '',
          c.obec_text || '',
          employeeRange(c.katpo),
        ].join('  '),
      );
      return haystack.includes(q);
    });
  }, [baseSet, filterText, lang]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const va = sortValue(a, sortKey, lang);
      const vb = sortValue(b, sortKey, lang);
      let cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
      if (cmp === 0) cmp = (a.firma || '').localeCompare(b.firma || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir, lang]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * PAGE_SIZE;
  const pageRows = sorted.slice(offset, offset + PAGE_SIZE);

  const sortIndicator = (key: SortKey) => {
    if (key !== sortKey)
      return <span className="ml-1 text-ctu-text-muted/40">↕</span>;
    return (
      <span className="ml-1 text-ctu-text-secondary">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const headCellClass =
    'px-3 py-2.5 text-left font-bold text-xs text-ctu-text-secondary whitespace-nowrap cursor-pointer select-none hover:bg-ctu-bg/70';

  function rowNavigate(c: CompanyListItem) {
    window.open(`/company/${c.id}`, '_blank', 'noopener,noreferrer');
  }

  function paginationItems(): (number | 'ellipsis')[] {
    const total = totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const set = new Set<number>([1, total, currentPage]);
    for (const o of [-1, 1]) set.add(currentPage + o);
    if (currentPage <= 3) [2, 3, 4].forEach((n) => set.add(n));
    if (currentPage >= total - 2)
      [total - 1, total - 2, total - 3].forEach((n) => set.add(n));
    const sortedNums = Array.from(set)
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    const out: (number | 'ellipsis')[] = [];
    for (let i = 0; i < sortedNums.length; i++) {
      out.push(sortedNums[i]);
      if (i < sortedNums.length - 1 && sortedNums[i + 1] - sortedNums[i] > 1) {
        out.push('ellipsis');
      }
    }
    return out;
  }

  return (
    <PageContainer>
      <PageContent>
        <CompanySearchBar
          onSearch={handleSearch}
          onClear={resetToBrowse}
          initialValue={barInitialValue}
        />

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {searchError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{searchError}</AlertDescription>
          </Alert>
        )}

        {(loading || searchLoading) && (
          <Card
            className="border-ctu-border shadow-none"
            aria-busy="true"
          >
            <CardContent className="px-6 pt-5 pb-4 flex flex-col gap-4">
              <Skeleton className="h-9 w-full max-w-md" />
              <div className="flex flex-col gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && !searchLoading && !searchError && companies.length > 0 && (
          <Card className="border-ctu-border shadow-none">
            <CardContent className="px-0 pt-4 pb-2">
              <div className="flex flex-col gap-3 px-6 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full max-w-md">
                  <Search
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ctu-text-muted"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder={t('results.filterPlaceholder')}
                    className="pl-8"
                  />
                </div>
                <div className="text-xs text-ctu-text-muted tabular-nums">
                  {t('results.found', { count: baseSet.length })}
                  {filterText.trim() && filtered.length !== baseSet.length && (
                    <span className="ml-1.5">
                      {t('results.filtered', { shown: filtered.length })}
                    </span>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="bg-ctu-bg">
                      <TableHead className={`${headCellClass} w-12 text-right`}>
                        {t('table.no')}
                      </TableHead>
                      {inSearchMode && (
                        <TableHead
                          className={`${headCellClass} w-28`}
                          onClick={() => handleSort('similarity')}
                        >
                          {t('table.similarity')}
                          {sortIndicator('similarity')}
                        </TableHead>
                      )}
                      <TableHead
                        className={headCellClass}
                        onClick={() => handleSort('firma')}
                      >
                        {t('table.companyName')}
                        {sortIndicator('firma')}
                      </TableHead>
                      <TableHead
                        className={headCellClass}
                        onClick={() => handleSort('nace')}
                      >
                        {t('table.nace')}
                        {sortIndicator('nace')}
                      </TableHead>
                      <TableHead
                        className={headCellClass}
                        onClick={() => handleSort('obec_text')}
                      >
                        {t('table.city')}
                        {sortIndicator('obec_text')}
                      </TableHead>
                      <TableHead
                        className={headCellClass}
                        onClick={() => handleSort('katpo')}
                      >
                        {t('table.employees')}
                        {sortIndicator('katpo')}
                      </TableHead>
                      {inSearchMode && (
                        <TableHead
                          className="px-3 py-2.5 w-10"
                          aria-label={t('table.competencies')}
                        />
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={inSearchMode ? 7 : 5}
                          className="px-3 py-10 text-center text-sm text-ctu-text-muted"
                        >
                          {t('results.noResults')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      pageRows.map((c, i) => {
                        const hasComps =
                          inSearchMode &&
                          Array.isArray(c.competencies) &&
                          c.competencies.length > 0;
                        const isExpanded =
                          c.id != null && expandedIds.has(c.id);
                        return (
                          <React.Fragment key={c.id ?? `${offset}-${i}`}>
                            <TableRow
                              onClick={() => rowNavigate(c)}
                              className="cursor-pointer hover:bg-muted/40"
                            >
                              <TableCell className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-ctu-text-muted">
                                {offset + i + 1}
                              </TableCell>
                              {inSearchMode && (
                                <TableCell className="px-3 py-2.5">
                                  <div className="inline-flex items-center gap-2">
                                    <span className="font-medium tabular-nums text-ctu-text">
                                      {typeof c.similarity === 'number'
                                        ? c.similarity.toFixed(2)
                                        : '—'}
                                    </span>
                                    <ScoreBar
                                      value={
                                        typeof c.similarity === 'number'
                                          ? c.similarity
                                          : null
                                      }
                                    />
                                  </div>
                                </TableCell>
                              )}
                              <TableCell className="px-3 py-2.5 font-medium text-ctu-text">
                                {c.firma}
                              </TableCell>
                              <TableCell className="px-3 py-2.5 text-ctu-text-secondary">
                                {(lang === 'cs' ? c.nace_cz : c.nace_en) || ''}
                              </TableCell>
                              <TableCell className="px-3 py-2.5 text-ctu-text-secondary">
                                {c.obec_text || ''}
                              </TableCell>
                              <TableCell className="px-3 py-2.5 text-ctu-text-secondary">
                                {employeeRange(c.katpo)}
                              </TableCell>
                              {inSearchMode && (
                                <TableCell className="px-2 py-2.5 text-right">
                                  {hasComps && (
                                    <button
                                      type="button"
                                      aria-label={t('table.competencies')}
                                      aria-expanded={isExpanded}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (c.id != null) toggleExpanded(c.id);
                                      }}
                                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ctu-text-muted hover:bg-ctu-bg hover:text-ctu-text"
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </button>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                            {hasComps && isExpanded && (
                              <TableRow className="bg-ctu-bg/40 hover:bg-ctu-bg/40">
                                <TableCell className="px-3 py-3" />
                                <TableCell colSpan={6} className="px-3 py-3">
                                  <SectionHeader
                                    label={t('table.competencies')}
                                    count={c.competencies!.length}
                                    tooltip={t('results.competenciesHint')}
                                  />
                                  <CompetencyBadges items={c.competencies!} />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {sorted.length > PAGE_SIZE && (
                <div className="flex flex-col items-center gap-2 px-6 pt-4 pb-2 sm:flex-row sm:justify-between">
                  <span className="text-xs text-ctu-text-muted tabular-nums">
                    {offset + 1}–{Math.min(offset + PAGE_SIZE, sorted.length)} /{' '}
                    {sorted.length}
                  </span>
                  <Pagination className="w-auto mx-0">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          aria-disabled={currentPage <= 1}
                          className={
                            currentPage <= 1
                              ? 'pointer-events-none opacity-40'
                              : ''
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) setPage(currentPage - 1);
                          }}
                        />
                      </PaginationItem>

                      {paginationItems().map((it, idx) =>
                        it === 'ellipsis' ? (
                          <PaginationItem key={`e-${idx}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={it}>
                            <PaginationLink
                              href="#"
                              isActive={it === currentPage}
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(it);
                              }}
                            >
                              {it}
                            </PaginationLink>
                          </PaginationItem>
                        ),
                      )}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          aria-disabled={currentPage >= totalPages}
                          className={
                            currentPage >= totalPages
                              ? 'pointer-events-none opacity-40'
                              : ''
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) setPage(currentPage + 1);
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!loading && companies.length === 0 && !error && (
          <Card className="border-ctu-border shadow-none">
            <CardContent className="py-10 text-center text-sm text-ctu-text-muted">
              {t('results.noCompanies')}
            </CardContent>
          </Card>
        )}
      </PageContent>
    </PageContainer>
  );
}
